// Lightweight i18n for the garage dashboard.
//
// Strings live in per-screen dictionaries under i18n/locales/<ns>.ts (each
// exports `sv` + `en`). The active language follows the tenant brand
// (GET /api/brand → locale). main.tsx loads the brand before first render, so
// t() returns the right language on the first paint.
//
// Usage:  import { t } from '../i18n';  t('login.title');  t('x.y', { n })
import { getBrand } from '../lib/brand'
import { namespaces } from './locales'

type Leaf = string
type Dict = { [key: string]: Leaf | Dict }

function buildDict(lang: 'sv' | 'en'): Dict {
  const out: Dict = {}
  for (const [ns, mod] of Object.entries(namespaces)) {
    out[ns] = ((mod as Record<string, Dict>)[lang] ?? {}) as Dict
  }
  return out
}

const SV = buildDict('sv')
const EN = buildDict('en')

function lookup(dict: Dict, key: string): string | undefined {
  let node: Leaf | Dict | undefined = dict
  for (const part of key.split('.')) {
    if (node == null || typeof node === 'string') return undefined
    node = node[part]
  }
  return typeof node === 'string' ? node : undefined
}

/** Translate `key` in the active locale (falls back to Swedish, then the key). */
export function t(key: string, vars?: Record<string, string | number>): string {
  const active = getBrand().locale === 'en' ? EN : SV
  const raw = lookup(active, key) ?? lookup(SV, key) ?? key
  if (!vars) return raw
  return raw.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  )
}
